import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus, X, Send, Paperclip, Loader2, Check, AlertTriangle, Ban,
  MessageSquare, Trash2, RotateCcw, Sparkles, Maximize2, Minimize2,
} from 'lucide-react';
import MicButton from './MicButton';
import ChatChart, { parseChartSpec } from './ChatChart';
import { claudeBuild } from '../services/api';
import {
  filesFromDrop, dragCarriesFiles, prepareAttachment, forStorage, prettySize,
} from '../lib/chatAttachments';

// The Claude Code panel: a manager types what they want changed in CPx and a build
// session picks it up, does it, and answers in the same chat.
//
// ⚠️ THE CHATS ARE THE FEATURE, not a nicety. Greg described exactly how this gets used:
// "he's probably gonna prompt, close it, and then look at what he's got... uses it a
// little more, realizes oh shoot I need to add something else, and comes back where
// there's context." So a chat is a durable thread, the panel opens on the most recent one,
// and a follow-up like "make it wider" is meaningful because the runner is handed the
// whole history. A stateless prompt box would have made every follow-up a re-explanation.
//
// ⚠️ THE BUTTON HIDES ITSELF for anyone without a row in cpx_build_access. `access`
// answers 200 with allowed:false for everybody rather than 403, so a colleague who does
// not have it sees no button instead of a broken one.

const POLL_MS = 4000;
const WORKING = new Set(['queued', 'building']);

// Markdown-lite. The runner writes reply.md in plain markdown, and this renders the few
// things it actually uses. Built as React nodes rather than innerHTML on purpose: the
// reply text is ours, but the ASK is free text a person typed and it renders in the same
// transcript, so nothing here is allowed to interpret markup as HTML.
function inline(text, keyBase) {
  const out = [];
  const re = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const k = `${keyBase}-i${i++}`;
    if (tok.startsWith('**')) {
      out.push(<strong key={k}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      out.push(
        <code key={k} style={{
          background: 'rgba(0,57,92,0.07)', borderRadius: 3, padding: '1px 5px',
          fontSize: '0.92em', fontFamily: 'ui-monospace, Consolas, monospace',
        }}>{tok.slice(1, -1)}</code>);
    } else {
      const cut = tok.indexOf('](');
      out.push(
        <a key={k} href={tok.slice(cut + 2, -1)} target="_blank" rel="noreferrer"
          style={{ color: 'var(--orange)', fontWeight: 600 }}>{tok.slice(1, cut)}</a>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Body({ text }) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let list = null;
  const flush = () => {
    if (list) {
      blocks.push(<ul key={`ul${blocks.length}`} style={{ margin: '6px 0', paddingLeft: 20 }}>{list}</ul>);
      list = null;
    }
  };
  // A ```chart block draws a chart (see ChatChart.js); any other fenced block is shown as
  // the plain text it is. Collected line by line so an unclosed fence still renders.
  let fence = null;
  const closeFence = (n) => {
    const raw = fence.lines.join('\n');
    const spec = fence.lang === 'chart' ? parseChartSpec(raw) : null;
    blocks.push(spec
      ? <ChatChart key={`c${n}`} spec={spec} />
      : <pre key={`f${n}`} className="cb-pre">{raw}</pre>);
    fence = null;
  };
  // A markdown table: rows of | cells |, with the |---| rule under the header dropped.
  let table = null;
  const flushTable = (n) => {
    if (!table) return;
    const rows = table.filter((r) => !r.every((c) => /^:?-{2,}:?$/.test(c)));
    table = null;
    if (!rows.length) return;
    const [head, ...rest] = rows;
    const numeric = (c) => /^-?[$(]?[\d,.]+%?\)?$/.test(c);
    blocks.push(
      <div key={`t${n}`} className="cb-table-wrap">
        <table className="cb-table">
          <thead><tr>{head.map((c, i) => <th key={i}>{inline(c, `th${n}-${i}`)}</th>)}</tr></thead>
          <tbody>
            {rest.map((r, ri) => (
              <tr key={ri}>
                {r.map((c, ci) => (
                  <td key={ci} style={numeric(c) ? { textAlign: 'right' } : undefined}>
                    {inline(c, `td${n}-${ri}-${ci}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>);
  };
  lines.forEach((raw, n) => {
    const line = raw.replace(/\s+$/, '');
    const tick = /^\s*```\s*([\w-]*)\s*$/.exec(line);
    if (fence) {
      if (tick) closeFence(n); else fence.lines.push(raw);
      return;
    }
    if (tick) { flush(); flushTable(n); fence = { lang: tick[1].toLowerCase(), lines: [] }; return; }
    if (/^\s*\|.*\|\s*$/.test(line)) {
      flush();
      table = table || [];
      table.push(line.trim().slice(1, -1).split('|').map((c) => c.trim()));
      return;
    }
    flushTable(n);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      list = list || [];
      list.push(<li key={`li${n}`} style={{ marginBottom: 3 }}>{inline(bullet[1], `b${n}`)}</li>);
      return;
    }
    flush();
    const head = /^(#{1,4})\s+(.*)$/.exec(line);
    if (head) {
      blocks.push(
        <div key={`h${n}`} style={{
          fontWeight: 700, color: 'var(--navy)', marginTop: blocks.length ? 12 : 0, marginBottom: 4,
        }}>{inline(head[2], `h${n}`)}</div>);
      return;
    }
    if (!line.trim()) {
      blocks.push(<div key={`s${n}`} style={{ height: 8 }} />);
      return;
    }
    blocks.push(<div key={`p${n}`} style={{ marginBottom: 2 }}>{inline(line, `p${n}`)}</div>);
  });
  flush();
  flushTable('end');
  if (fence) closeFence('end');
  return <>{blocks}</>;
}

const StatusChip = ({ status }) => {
  if (!status) return null;
  const map = {
    queued: ['Queued', <Loader2 key="i" size={12} className="cb-spin" />, '#8a6d3b', '#fcf3e3'],
    building: ['Working on it', <Loader2 key="i" size={12} className="cb-spin" />, '#8a6d3b', '#fcf3e3'],
    done: ['Done', <Check key="i" size={12} />, '#1d6b3f', '#e7f5ec'],
    blocked: ['Needs Greg', <Ban key="i" size={12} />, '#8a4b1f', '#fdece2'],
    failed: ['That run broke', <AlertTriangle key="i" size={12} />, '#9b2c2c', '#fdeaea'],
  };
  const [label, icon, fg, bg] = map[status] || [status, null, '#555', '#eee'];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, background: bg, color: fg,
      borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700,
      letterSpacing: 0.2, textTransform: 'uppercase',
    }}>{icon}{label}</span>
  );
};

// The live feed while a build runs. Greg asked for this after watching the panel sit on
// "WORKING ON IT" and explained what he wanted it for: to see what is being worked on, so
// he can tell it read the ask right, and to sense how close it is to finishing.
//
// Newest at the BOTTOM and auto-scrolled, so it reads like a transcript rather than making
// you hunt for the current step. Thinking is dimmed and italic, the steps are plain, and
// what it says to you is in full colour - three different things, and flattening them into
// one grey list would bury the one line that actually addresses you.
function Progress({ items, since }) {
  const boxRef = useRef(null);
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [items]);

  // Elapsed answers "how close" better than anything else when the feed is quiet, which is
  // exactly when somebody starts wondering whether it is stuck.
  useEffect(() => {
    if (!since) return undefined;
    const tick = () => {
      const s = Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 1000));
      setElapsed(s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [since]);

  const style = {
    thinking: { color: '#7a8b96', fontStyle: 'italic' },
    saying: { color: '#22404f' },
    doing: { color: '#33505f' },
    finished: { color: '#1d6b3f', fontWeight: 600 },
  };

  return (
    <div className="cb-progress">
      <div className="cb-progress-head">
        <span>What it is doing</span>
        {elapsed && <span className="cb-elapsed">{elapsed}</span>}
      </div>
      <div className="cb-progress-body" ref={boxRef}>
        {!items.length && (
          <div style={style.thinking}>Starting up. The first step usually takes a moment.</div>
        )}
        {items.map((p, i) => (
          <div key={i} className="cb-step" style={style[p.kind] || style.doing}>
            <span className="cb-step-mark">
              {p.kind === 'doing' ? '·' : p.kind === 'thinking' ? '~' : p.kind === 'finished' ? '✓' : '›'}
            </span>
            <span>{p.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ClaudeBuild({ currentPage }) {
  const [allowed, setAllowed] = useState(false);
  const [pages, setPages] = useState([]);
  const [open, setOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [chat, setChat] = useState(null);
  const [text, setText] = useState('');
  const [atts, setAtts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dragging, setDragging] = useState(false);
  // Full screen, for reading charts and tables. Remembered per browser, because somebody
  // who works in it full screen wants it to open that way next time too.
  const [full, setFull] = useState(() => {
    try { return localStorage.getItem('cpxClaudeBuildFull') === '1'; } catch (_) { return false; }
  });
  const toggleFull = () => setFull((v) => {
    try { localStorage.setItem('cpxClaudeBuildFull', v ? '0' : '1'); } catch (_) { /* fine */ }
    return !v;
  });
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  // Bumped on send so MicButton switches dictation off. See the note there.
  const [micStop, setMicStop] = useState(0);

  // Dictation puts the words in the box, and the caret has to follow them or Enter has
  // nothing to act on. Greg, 2026-09-11: "I hit the mic button, I talk, and I wanna be
  // able to just slap the enter key, but I have to click back in the box." MicButton no
  // longer steals focus when clicked, and this covers the other case - opening the panel
  // and going straight to the mic, where the box was never focused to begin with.
  const takeDictation = useCallback((chunk) => {
    setText((prev) => {
      const next = prev ? `${prev} ${chunk}` : chunk;
      // After the value lands, so the caret goes after the new words rather than
      // wherever it happened to be.
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        try { el.setSelectionRange(next.length, next.length); } catch (_) { /* not fatal */ }
      });
      return next;
    });
  }, []);
  const pollRef = useRef(null);

  useEffect(() => {
    claudeBuild('access')
      .then((r) => { setAllowed(!!r.allowed); setPages(r.pages || []); })
      .catch(() => setAllowed(false));
  }, []);

  const loadChats = useCallback(async () => {
    const r = await claudeBuild('chats');
    setChats(r.chats || []);
    return r.chats || [];
  }, []);

  const loadChat = useCallback(async (id) => {
    if (!id) { setChat(null); return null; }
    const r = await claudeBuild('chat', { chatId: id });
    setChat(r.chat || null);
    return r.chat || null;
  }, []);

  // Opening lands on the most recent thread, because the usual reason for opening this is
  // to see what came back from the last ask.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const list = await loadChats();
        if (!chatId && list.length) {
          setChatId(list[0].id);
          await loadChat(list[0].id);
        }
      } catch (e) { setErr(e.message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Poll only while something is actually running, and stop the moment it is not - an
  // always-on interval against a Neon-backed endpoint is a slow tax on every open tab.
  const working = !!(chat && chat.messages || []).slice().reverse()
    .find((m) => m.job_status && WORKING.has(m.job_status));

  useEffect(() => {
    clearInterval(pollRef.current);
    if (!open || !working || !chatId) return undefined;
    pollRef.current = setInterval(() => {
      loadChat(chatId).catch(() => {});
      loadChats().catch(() => {});
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [open, working, chatId, loadChat, loadChats]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [chat]);

  const addFiles = useCallback(async (files) => {
    for (const f of files) {
      try {
        // store:true forces the original into R2 - the runner is a separate process on
        // another machine and a base64 blob in this tab is no use to it.
        // Awaited BEFORE setAtts: a state updater is not an async function, so the await
        // has to finish out here. The functional form still matters, because two files
        // dropped together resolve in whatever order R2 answers.
        const att = await prepareAttachment(f, { prefix: 'claude-build', store: true });
        setAtts((a) => [...a, att]);
      } catch (e) { setErr(e.message); }
    }
  }, []);

  const send = async () => {
    if (busy || (!text.trim() && !atts.length)) return;
    // Switch dictation off on the way out. The recognizer is continuous, so otherwise it
    // keeps listening after the message goes and the next thing said in the room lands in
    // the empty box.
    setMicStop((n) => n + 1);
    setBusy(true); setErr('');
    try {
      const r = await claudeBuild('send', {
        chatId: chatId || undefined,
        page: currentPage,
        body: text.trim(),
        attachments: atts.map(forStorage),
      });
      setText(''); setAtts([]);
      setChatId(r.chatId);
      await loadChat(r.chatId);
      await loadChats();
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  const pick = async (id) => {
    setChatId(id); setErr('');
    try { await loadChat(id); } catch (e) { setErr(e.message); }
  };

  const startNew = () => { setChatId(null); setChat(null); setText(''); setAtts([]); setErr(''); };

  const archive = async (id) => {
    try {
      await claudeBuild('archive', { chatId: id });
      const list = await loadChats();
      if (id === chatId) {
        setChatId(list[0]?.id || null);
        await loadChat(list[0]?.id || null);
      }
    } catch (e) { setErr(e.message); }
  };

  const retry = async (jobId) => {
    try { await claudeBuild('retry', { jobId }); await loadChat(chatId); } catch (e) { setErr(e.message); }
  };

  // ⚠️ THE BUTTON ONLY APPEARS ON THE PAGES THIS PERSON LOOKS AFTER. Greg, 2026-09-10:
  // *"the button showing that page and the scope should be limited to that page."* Owners
  // get '*' and see it everywhere.
  //
  // This is presentation, not the boundary. `send` re-checks the page against the grant
  // server-side, and the build itself is held to a file allowlist - because a hidden
  // button stops nobody who can open a network tab.
  const inScope = allowed && (pages === '*' || (Array.isArray(pages) && pages.includes(currentPage)));
  if (!inScope) return null;

  if (!open) {
    return (
      <button className="cb-fab" onClick={() => setOpen(true)} title="Ask Claude to change something in CPx">
        <Sparkles size={17} />
        <span>Claude Code</span>
      </button>
    );
  }

  const msgs = (chat && chat.messages) || [];

  return (
    <div className={`cb-scrim${full ? ' full' : ''}`} onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div
        className={`cb-panel${full ? ' full' : ''}`}
        onDragOver={(e) => { if (dragCarriesFiles(e.dataTransfer)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragging(true); } }}
        onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false); }}
        onDrop={async (e) => {
          if (!dragCarriesFiles(e.dataTransfer)) return;
          e.preventDefault(); setDragging(false);
          // filesFromDrop resolves the Outlook virtual-file case, where dataTransfer.files
          // is empty AND getAsFile() is null and only webkitGetAsEntry() gives you the file.
          await addFiles(await filesFromDrop(e.dataTransfer));
        }}
      >
        <aside className="cb-side">
          <div className="cb-side-head">
            <button className="cb-new" onClick={startNew}><Plus size={14} /> New chat</button>
          </div>
          <div className="cb-list">
            {!chats.length && <div className="cb-empty-side">Nothing yet. Ask for something.</div>}
            {chats.map((c) => (
              <div key={c.id} className={`cb-item${c.id === chatId ? ' on' : ''}`} onClick={() => pick(c.id)}>
                <MessageSquare size={13} style={{ flexShrink: 0, opacity: 0.55 }} />
                <span className="cb-item-title">{c.title}</span>
                {WORKING.has(c.last_status) && <Loader2 size={12} className="cb-spin" style={{ flexShrink: 0 }} />}
                <button className="cb-kill" title="Archive this chat"
                  onClick={(e) => { e.stopPropagation(); archive(c.id); }}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </aside>

        <main className="cb-main">
          <header className="cb-head">
            <div>
              <div className="cb-title">{chat ? chat.title : 'New request'}</div>
              <div className="cb-sub">Ask a question or ask for a change in CPx. The answer lands here.</div>
            </div>
            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
              <button className="cb-x cb-full-btn" onClick={toggleFull} title={full ? 'Back to window size' : 'Full screen'}>
                {full ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
              </button>
              <button className="cb-x" onClick={() => setOpen(false)} title="Close"><X size={18} /></button>
            </div>
          </header>

          <div className={`cb-thread${dragging ? ' drag' : ''}`}>
            {!msgs.length && (
              <div className="cb-empty">
                <Sparkles size={22} style={{ color: 'var(--orange)' }} />
                <p><strong>Say what you want changed.</strong></p>
                <p className="cb-hint">
                  Plain English is fine, and a screenshot is often quicker than describing it.
                  Drop or paste one straight in. You can close this and come back. The answer
                  will be waiting in the chat.
                </p>
              </div>
            )}

            {msgs.map((m) => (
              <div key={m.id} className={`cb-msg ${m.role}`}>
                <div className="cb-bubble">
                  <Body text={m.body} />
                  {!!(m.attachments || []).length && (
                    <div className="cb-atts">
                      {(m.attachments || []).map((a, i) => (
                        <span key={i} className="cb-att"><Paperclip size={11} />{a.name}</span>
                      ))}
                    </div>
                  )}
                </div>
                {m.role === 'user' && m.job_status && (
                  <div className="cb-meta">
                    <StatusChip status={m.job_status} />
                    {m.commit_sha && <span className="cb-sha">{String(m.commit_sha).slice(0, 7)}</span>}
                    {m.branch && <span className="cb-sha">branch {m.branch}</span>}
                    {m.job_status === 'failed' && (
                      <button className="cb-retry" onClick={() => retry(m.job_id)}>
                        <RotateCcw size={11} /> Retry
                      </button>
                    )}
                  </div>
                )}
                {m.job_id && m.job_id === (chat && chat.liveJobId) && (
                  <Progress items={(chat && chat.progress) || []} since={m.claimed_at || m.created_at} />
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {err && <div className="cb-err">{err}</div>}

          <div className="cb-composer">
            {!!atts.length && (
              <div className="cb-pending">
                {atts.map((a, i) => (
                  <span key={i} className={`cb-att${a.readable ? '' : ' bad'}`}>
                    <Paperclip size={11} />{a.name}
                    {a.bytes ? ` (${prettySize(a.bytes)})` : ''}
                    <button onClick={() => setAtts((p) => p.filter((_, n) => n !== i))}><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="cb-input-row">
              <textarea
                ref={inputRef}
                className="cb-input cpx-touch"
                value={text}
                placeholder={working
                  ? 'Working on the last one. It will land above when it is done.'
                  : 'What do you want changed?'}
                disabled={working}
                onChange={(e) => setText(e.target.value)}
                onPaste={async (e) => {
                  const files = Array.from(e.clipboardData?.files || []);
                  if (files.length) { e.preventDefault(); await addFiles(files); }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={2}
              />
              <div className="cb-actions">
                <MicButton onTranscript={takeDictation} stopWhen={micStop} size={15} />
                <button className="cb-clip" title="Attach a screenshot or file"
                  onClick={() => fileRef.current?.click()}><Paperclip size={15} /></button>
                <button className="cb-send" onClick={send}
                  disabled={busy || working || (!text.trim() && !atts.length)} title="Send">
                  {busy ? <Loader2 size={15} className="cb-spin" /> : <Send size={15} />}
                </button>
              </div>
            </div>
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
              onChange={async (e) => { await addFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
            <div className="cb-foot">Enter sends. Shift and Enter starts a new line.</div>
          </div>
        </main>
      </div>
    </div>
  );
}
